import { defineStore } from 'pinia';
import type {
  IGameCategoriesV4Data,
  IGameCategory,
} from '~/interfaces/dto/games/response';
import type { IFixedJackpotEvent } from '~/interfaces/jackpots/fixed';
import type { GameStoreInterface } from '~/interfaces/stores/games.store.interface';
import type { IAllWinnerCategories } from '~/interfaces/winners/winnersInterfaces';
export const useGameStore = defineStore('games', {
  state: (): GameStoreInterface => {
    return {
      verticalData: null,
      categoriesAndGames: [],
      categories: [],
      themes: [],
      providers: [],
      buildVersion: '0',
      disabledProviders: [],
      hiddenProviders: [],
      favorites: [],
      favoriteIds: [],
      mostLiked: [],
      recommended: [],
      currentGame: null,
      currentCategory: null,
      recentlyPlayed: [],
      recentSearches: [],
      winnersCircle: null,
      fixedJackpots: [],
      inGameFixedJackpotEventId: '',
    };
  },
  actions: {
    setGameStore(data: IGameCategoriesV4Data) {
      this.verticalData = data;
      if (!data?.categories) return;
      this.categoriesAndGames = data?.categories;
      if (data?.providers?.length) {
        this.providers = [...data.providers];
      }
      this.themes = [...data.themes];

      this.categories = this.categoriesAndGames?.map(
        (category) => category.name,
      );
    },
    setCurrentGame(game: IGameCategory) {
      this.currentGame = game;
    },
    setCurrentCategory(category: string) {
      this.currentCategory = category;
    },
    setFavorites(data: Array<IGameCategory>) {
      if (!data) return;
      this.favorites = data;
      this.favoriteIds = data?.map((val) => val.id);
    },
    setRecommended(data: Array<IGameCategory>) {
      if (!data) return;
      this.recommended = data;
    },
    setMostLiked(data: Array<IGameCategory>) {
      if (!data) return;
      this.mostLiked = data;
    },
    setRecentlyPlayed(games: Array<IGameCategory>) {
      this.recentlyPlayed = games;
    },
    setRecentSearches(searches: Array<string>) {
      this.recentSearches = searches;
    },
    clearGameState() {
      this.currentGame = {};
    },
    clearCompleteState() {
      this.categoriesAndGames = [];
      this.currentCategory = null;
      this.categories = [];
      this.themes = [];
    },
    setCurrentGameLikes(likes: number) {
      if (!(likes < 0)) {
        this.currentGame.likesCount = likes;
      }
    },
    setBuildInformation({
      buildVersion,
      hiddenProviders,
      disabledProviders,
    }: {
      buildVersion: string;
      hiddenProviders: string[];
      disabledProviders: string[];
    }) {
      this.buildVersion = buildVersion;
      this.hiddenProviders = [...hiddenProviders];
      this.disabledProviders = [...disabledProviders];
    },
    setFixedJackpot(data: IFixedJackpotEvent[]) {
      this.fixedJackpots = data;
    },
    setInGameFixedJackpotEventId(data: string) {
      this.inGameFixedJackpotEventId = data;
    },
  },
  getters: {
    returnCategoriesAndGames(state) {
      return state.categoriesAndGames;
    },
    returnCategories(state) {
      return state.categories;
    },
    returnProviders(state) {
      return state.providers;
    },
    returnFullCategory(state) {
      return (category: string) => {
        return state.categoriesAndGames.find(({ name }) => name === category);
      };
    },
    returnCurrentCategory(state) {
      return state.currentCategory;
    },
    returnCategory(state) {
      return ({ category }) => {
        if (category === 'favorites') return state.favorites;
        if (category === 'recentlyPlayed') return state.recentlyPlayed;

        return (
          state.categoriesAndGames?.find(({ name }) => name === category)
            ?.games || []
        );
      };
    },
    isFavorite(state) {
      return (id: string) => {
        if (!state.favoriteIds.length) return false;
        return state.favoriteIds.some((fav) => fav === id);
      };
    },
    returnFavorites(state) {
      return state.favorites?.filter(
        (game) =>
          ![...state.disabledProviders, ...state.hiddenProviders].includes(
            game?.provider,
          ),
      );
    },
    returnRecommended(state) {
      return state.recommended.filter(
        (game) =>
          ![...state.disabledProviders, ...state.hiddenProviders].includes(
            game?.provider,
          ),
      );
    },
    returnMostLiked(state) {
      return state.mostLiked.filter(
        (game) =>
          ![...state.disabledProviders, ...state.hiddenProviders].includes(
            game?.provider,
          ),
      );
    },
    returnRecentlyPlayed(state) {
      return (
        state.recentlyPlayed?.filter(
          (game) =>
            ![...state.disabledProviders, ...state.hiddenProviders].includes(
              game?.provider,
            ),
        ) || []
      );
    },
    returnRecentSearches(state) {
      return state.recentSearches;
    },
    returnFavoriteIds(state) {
      return state.favoriteIds;
    },
    returnCurrentGame(state) {
      return state.currentGame;
    },
    returnCurrentGameLikes(state) {
      return state.currentGame?.likesCount;
    },
    returnHiddenProviders(state) {
      return state.hiddenProviders;
    },
    returnDisabledProviders(state) {
      return state.disabledProviders;
    },
    returnFixedJackpots(state) {
      return (category?: string) => {
        if (!category) return state.fixedJackpots;
        return state.fixedJackpots.filter(
          (jackpots) => jackpots.gameCategory === category,
        );
      };
    },
    returnInGameFixedJackpotEventId(state) {
      return state.inGameFixedJackpotEventId;
    },
  },
});
